import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Footer } from '../components/shared/Footer';
import { getQuoteOfTheDay } from '../components/shared/Quotes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Composant compteur animé style slot machine
const AnimatedCounter = ({ target, duration = 2000 }) => {
  const [current, setCurrent] = useState(0);
  const startTime = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (target <= 0) return;
    startTime.current = performance.now();
    
    const animate = (now) => {
      const elapsed = now - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      // Easing: ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  const digits = String(current).split('');
  
  return (
    <div className="flex items-center justify-center gap-[3px]">
      {digits.map((d, i) => (
        <span 
          key={i}
          className="inline-block w-[36px] h-[48px] leading-[48px] text-center text-[36px] font-black text-white rounded-lg"
          style={{ 
            background: 'rgba(255,255,255,0.12)',
            textShadow: '0 2px 8px rgba(0,0,0,0.3)',
            animation: current === target ? `counterPop 0.3s ease-out ${i * 0.05}s both` : 'none'
          }}
        >
          {d}
        </span>
      ))}
    </div>
  );
};

export const Welcome = () => {
  const navigate = useNavigate();
  const [displayDate, setDisplayDate] = useState(new Date());
  const [isTomorrow, setIsTomorrow] = useState(false);
  const [activeSpecialPages, setActiveSpecialPages] = useState([]);
  const [weather, setWeather] = useState(null);
  const [dishCount, setDishCount] = useState(0);
  const [lastUpdatedAgo, setLastUpdatedAgo] = useState('');

  const getLocalDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getWeatherIcon = (code) => {
    if (code === 0) return '☀️';
    if (code <= 3) return '⛅';
    if (code <= 48) return '🌫️';
    if (code <= 57) return '🌦️';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '🌨️';
    if (code <= 82) return '🌧️';
    if (code <= 86) return '🌨️';
    if (code >= 95) return '⛈️';
    return '☁️';
  };

  useEffect(() => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    let targetDate = now;
    let tomorrow = false;
    if (currentHour > 13 || (currentHour === 13 && currentMinutes >= 30)) {
      targetDate = new Date(now);
      targetDate.setDate(targetDate.getDate() + 1);
      tomorrow = true;
    }
    setDisplayDate(targetDate);
    setIsTomorrow(tomorrow);

    axios.get(`${API}/special-pages?active_only=true`)
      .then(res => setActiveSpecialPages(res.data.filter(p => p.is_active)))
      .catch((err) => { console.error('Special pages load failed:', err); });

    // Compteur de plats du jour
    axios.get(`${API}/dishes/count`)
      .then(res => setDishCount(res.data.count || 0))
      .catch(() => {});

    // Dernière mise à jour
    axios.get(`${API}/dishes/last-updated`)
      .then(res => {
        const last = new Date(res.data.last_updated);
        const diffMs = Date.now() - last.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) setLastUpdatedAgo("à l'instant");
        else if (diffMin < 60) setLastUpdatedAgo(`il y a ${diffMin}min`);
        else {
          const diffH = Math.floor(diffMin / 60);
          if (diffH < 24) setLastUpdatedAgo(`il y a ${diffH}h`);
          else {
            const diffD = Math.floor(diffH / 24);
            setLastUpdatedAgo(`il y a ${diffD}j`);
          }
        }
      })
      .catch(() => {});

    // Météo Open-Meteo (Genève-Lausanne, gratuit, sans clé)
    const dateStr = getLocalDateStr(targetDate);
    if (tomorrow) {
      // Demain → température max prévue
      axios.get(`https://api.open-meteo.com/v1/forecast?latitude=46.2&longitude=6.15&daily=temperature_2m_max,weathercode&timezone=Europe/Zurich&start_date=${dateStr}&end_date=${dateStr}`)
        .then(res => {
          const data = res.data?.daily;
          if (data) {
            setWeather({
              temp: Math.round(data.temperature_2m_max[0]),
              icon: getWeatherIcon(data.weathercode[0]),
              label: 'Prévision'
            });
          }
        })
        .catch((err) => { console.error('Weather forecast failed:', err); });
    } else {
      // Aujourd'hui → température actuelle
      axios.get(`https://api.open-meteo.com/v1/forecast?latitude=46.2&longitude=6.15&current_weather=true&timezone=Europe/Zurich`)
        .then(res => {
          const cw = res.data?.current_weather;
          if (cw) {
            setWeather({
              temp: Math.round(cw.temperature),
              icon: getWeatherIcon(cw.weathercode),
              label: 'Actuellement'
            });
          }
        })
        .catch((err) => { console.error('Weather current failed:', err); });
    }
  }, []);

  const handleEnter = () => {
    const dateStr = getLocalDateStr(displayDate);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => navigate(`/plats?date=${dateStr}&sort=distance`),
        () => navigate(`/plats?date=${dateStr}&sort=alphabetical`),
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    } else {
      navigate(`/plats?date=${dateStr}&sort=alphabetical`);
    }
  };

  const formattedDate = displayDate.toLocaleDateString('fr-FR', { 
    weekday: 'long', day: 'numeric', month: 'long' 
  });

  const quote = getQuoteOfTheDay(displayDate);

  // Couleurs estivales du bouton — change chaque jour
  const BUTTON_COLORS = [
    { bg: 'linear-gradient(180deg, #E8C547 0%, #D4AF37 50%, #C9A227 100%)', text: '#3D2E1C', shadow: 'rgba(212, 175, 55, 0.5)' },      // Or (défaut)
    { bg: 'linear-gradient(180deg, #FF9A6C 0%, #FF6B4A 50%, #E8533A 100%)', text: '#ffffff', shadow: 'rgba(255, 107, 74, 0.5)' },       // Corail
    { bg: 'linear-gradient(180deg, #7DD3A8 0%, #4CAF82 50%, #3D9E72 100%)', text: '#ffffff', shadow: 'rgba(76, 175, 130, 0.5)' },       // Menthe
    { bg: 'linear-gradient(180deg, #FFD166 0%, #F4B740 50%, #E5A82E 100%)', text: '#3D2E1C', shadow: 'rgba(244, 183, 64, 0.5)' },       // Mangue
    { bg: 'linear-gradient(180deg, #A8D8EA 0%, #68B8D7 50%, #4FA8C7 100%)', text: '#1A3A4A', shadow: 'rgba(104, 184, 215, 0.5)' },      // Ciel
    { bg: 'linear-gradient(180deg, #F9A8D4 0%, #EC4899 50%, #DB2777 100%)', text: '#ffffff', shadow: 'rgba(236, 72, 153, 0.5)' },       // Framboise
    { bg: 'linear-gradient(180deg, #C4B5FD 0%, #8B5CF6 50%, #7C3AED 100%)', text: '#ffffff', shadow: 'rgba(139, 92, 246, 0.5)' },       // Lavande
  ];
  const dayOfYear = Math.floor((displayDate - new Date(displayDate.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const btnColor = BUTTON_COLORS[dayOfYear % BUTTON_COLORS.length];

  return (
    <div className="min-h-screen flex flex-col relative" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      
      {/* ============ VERSION MOBILE — Capsule verte sur photo ============ */}
      <div className="md:hidden min-h-screen flex flex-col relative">
        
        {/* Photo de fond */}
        <div 
          className="absolute inset-0 z-0"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_6ee05899-d7ab-4f24-b666-fce2e0d15582/artifacts/gaj5v8ox_IMG_0658.JPG)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top'
          }}
        ></div>
        
        {/* Header */}
        <header className="px-6 pt-4 text-center relative z-10">
          <span className="text-sm font-bold text-white tracking-wide drop-shadow-lg">
            AUMENU<span className="text-[#D4AF37]">.CH</span>
          </span>
        </header>

        {/* Contenu principal */}
        <main className="flex-1 flex flex-col items-center px-5 py-4 relative z-10">
          
          {/* Capsule automnale */}
          <div 
            className="mt-8 w-full max-w-[330px] rounded-[20px] px-7 py-8 text-center"
            style={{
              background: 'linear-gradient(160deg, #C4956A 0%, #A67B5B 40%, #8B6142 100%)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}
            data-testid="welcome-card"
          >
            {/* Compteur animé */}
            {dishCount > 0 && (
              <div className="mb-3" style={{ animation: 'fadeInUp 0.6s ease-out both' }}>
                <AnimatedCounter target={dishCount} duration={2200} />
                {lastUpdatedAgo && (
                  <p className="text-white/50 text-[10px] mt-1.5 tracking-wide">màj {lastUpdatedAgo}</p>
                )}
              </div>
            )}

            <h1 className="text-lg font-extrabold text-white leading-tight">
              <span className="inline-block" style={{ animation: 'slideInLeft 0.6s ease-out both' }}>Plats du jour en ligne</span><br />
              <span className="inline-block text-[26px]" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                {isTomorrow ? "demain" : "aujourd'hui"} à midi
              </span>
            </h1>
            
            {/* Date */}
            <p 
              className="inline-block mt-4 text-[15px] font-semibold capitalize px-5 py-1.5 rounded-full"
              style={{ background: btnColor.bg, color: btnColor.text }}
            >
              {formattedDate}
            </p>

            {/* Météo */}
            {weather && (
              <p className="mt-2 text-white/70 text-[12px]" data-testid="weather-info">
                {weather.label} : {weather.icon} {weather.temp}°C
              </p>
            )}
            
            {/* Citation du jour */}
            <p className="mt-4 text-white/80 text-[13px] leading-relaxed italic h-[40px] flex items-center justify-center" data-testid="daily-quote">
              "{quote.text}" — {quote.author}
            </p>

            {/* Bouton */}
            <div className="mt-4">
              <button
                onClick={handleEnter}
                data-testid="enter-btn-mobile"
                className="relative group font-bold text-[17px] py-3.5 px-10 rounded-full transition-all duration-300 hover:scale-105 active:scale-95 overflow-hidden"
                style={{
                  background: btnColor.bg,
                  color: btnColor.text,
                  boxShadow: `0 4px 30px ${btnColor.shadow}`,
                }}
              >
                <span className="relative z-10">J'ai faim !</span>
                <div 
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)',
                    animation: 'shimmer 2.5s infinite 0.5s'
                  }}
                ></div>
              </button>
            </div>

            {/* Sous-titre */}
            <p className="mt-4 text-white/60 text-[11px]">
              Plus de 100 restaurants entre Genève et Lausanne
            </p>
          </div>

          {/* Badge pages spéciales */}
          {activeSpecialPages.length > 0 && activeSpecialPages.map(sp => (
            <Link
              key={sp._id || sp.id}
              to={`/fete/${sp.slug}`}
              className="mt-4"
              data-testid={`special-badge-mobile-${sp.slug}`}
            >
              <div className="flex items-center gap-2.5 bg-black/50 backdrop-blur-sm rounded-full pl-3 pr-5 py-2 border border-[#D4AF37]/60 shadow-xl animate-bounce-slow hover:scale-105 transition-transform duration-300">
                <span className="text-lg">🎉</span>
                <span className="text-white text-xs font-semibold whitespace-nowrap">
                  Découvrez les Menus — {sp.title} <span className="text-[#D4AF37]">&rarr;</span>
                </span>
              </div>
            </Link>
          ))}

          <div className="flex-1"></div>
        </main>

        <Footer showTitle={false} className="px-6 py-3" />

        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes slideInLeft {
            0% { transform: translateX(-30px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes fadeInUp {
            0% { transform: translateY(15px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes counterPop {
            0% { transform: scale(1); }
            50% { transform: scale(1.08); }
            100% { transform: scale(1); }
          }
        `}</style>
      </div>

      {/* ============ VERSION DESKTOP — Même design capsule verte ============ */}
      <div className="hidden md:flex md:flex-col md:min-h-screen">
        {/* Photo de fond desktop */}
        <div 
          className="fixed inset-0 z-0"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_6ee05899-d7ab-4f24-b666-fce2e0d15582/artifacts/gaj5v8ox_IMG_0658.JPG)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top'
          }}
        ></div>

        {/* Header Desktop */}
        <header className="px-6 py-4 relative z-10">
          <div className="flex items-center justify-center">
            <span className="text-xl font-bold text-white tracking-tight drop-shadow-lg">
              AUMENU<span className="text-[#D4AF37]">.CH</span>
            </span>
          </div>
        </header>

        {/* Capsule automnale centrée */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 relative z-10">
          <div 
            className="w-full max-w-[400px] rounded-[24px] px-10 py-10 text-center"
            style={{
              background: 'linear-gradient(160deg, #C4956A 0%, #A67B5B 40%, #8B6142 100%)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.3)'
            }}
            data-testid="welcome-card-desktop"
          >
            {/* Compteur animé desktop */}
            {dishCount > 0 && (
              <div className="mb-4" style={{ animation: 'fadeInUp 0.6s ease-out both' }}>
                <AnimatedCounter target={dishCount} duration={2200} />
                {lastUpdatedAgo && (
                  <p className="text-white/50 text-xs mt-2 tracking-wide">màj {lastUpdatedAgo}</p>
                )}
              </div>
            )}

            <h1 className="font-extrabold text-white leading-tight mb-4">
              <span className="text-2xl inline-block" style={{ animation: 'slideInLeft 0.6s ease-out both' }}>Plats du jour en ligne</span><br />
              <span className="text-5xl inline-block" style={{ animation: 'fadeInUp 0.5s ease-out 0.4s both' }}>
                {isTomorrow ? "demain" : "aujourd'hui"} à midi
              </span>
            </h1>

            <p 
              className="inline-block text-lg font-semibold capitalize px-6 py-1.5 rounded-full"
              style={{ background: btnColor.bg, color: btnColor.text }}
            >
              {formattedDate}
            </p>

            {weather && (
              <p className="mt-2 text-white/70 text-sm" data-testid="weather-info-desktop">
                {weather.label} : {weather.icon} {weather.temp}°C
              </p>
            )}

            <p className="mt-5 text-white/80 text-base leading-relaxed italic h-[48px] flex items-center justify-center" data-testid="daily-quote-desktop">
              "{quote.text}" — {quote.author}
            </p>

            <div className="mt-5">
              <button
                onClick={handleEnter}
                data-testid="enter-btn"
                className="relative group font-bold text-xl py-5 px-12 rounded-full transition-all duration-300 hover:scale-105 active:scale-95 overflow-hidden"
                style={{
                  background: btnColor.bg,
                  color: btnColor.text,
                  boxShadow: `0 4px 40px ${btnColor.shadow}`,
                }}
              >
                <span className="relative z-10">J'ai faim !</span>
                <div 
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)',
                    animation: 'shimmer 2.5s infinite 0.5s'
                  }}
                ></div>
              </button>
            </div>

            {/* Sous-titre */}
            <p className="mt-4 text-white/60 text-sm">
              Plus de 100 restaurants entre Genève et Lausanne
            </p>
          </div>

          {activeSpecialPages.length > 0 && activeSpecialPages.map(sp => (
            <Link
              key={sp._id || sp.id}
              to={`/fete/${sp.slug}`}
              className="mt-6"
              data-testid={`special-badge-desktop-${sp.slug}`}
            >
              <div className="flex items-center gap-3 bg-black/50 backdrop-blur-sm rounded-full pl-4 pr-6 py-2.5 border border-[#D4AF37]/60 shadow-xl animate-bounce-slow hover:scale-105 transition-transform duration-300">
                <span className="text-2xl">🎉</span>
                <span className="text-white text-base font-semibold whitespace-nowrap">
                  Découvrez les Menus — {sp.title} <span className="text-[#D4AF37]">&rarr;</span>
                </span>
              </div>
            </Link>
          ))}
          
          <style>{`
            @keyframes shimmer {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            @keyframes slideInLeft {
              0% { transform: translateX(-30px); opacity: 0; }
              100% { transform: translateX(0); opacity: 1; }
            }
            @keyframes fadeInUp {
              0% { transform: translateY(15px); opacity: 0; }
              100% { transform: translateY(0); opacity: 1; }
            }
          `}</style>
        </div>

        <Footer showTitle={false} className="px-6 py-6" />
      </div>
    </div>
  );
};
